import { HomeCard } from "./HomeCard";
import { useGetRecruiterCount } from "../hooks/useGetRecruiterCount";
import { useGetRecruiterPriorityActions } from "../hooks/useGetRecruiterPriorityActions";
import { useState, useEffect } from "react";
import { RecruiterProgress } from "./RecruiterProgress";
import { RecruiterChart } from "./RecruiterChart";
import { DashboardShimmer } from "components/layout/components/loaders/Shimmer";
import { PriorityActionCard } from "./PriorityActionCard";
import { routePath } from "config/routeMgt/routePath";
import { useNavigate } from "react-router-dom";

export const RecruiterCount = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useGetRecruiterCount();
  const {
    data: priorityActions,
    isLoading: isPriorityLoading,
    isError: isPriorityError,
  } = useGetRecruiterPriorityActions();
  const [isOpen, setIsOpen] = useState(false);

  const total = data?.sent_invites ?? 0;
  const accepted = data?.accepted_invites ?? 0;
  const pending = data?.pending_invites ?? 0;
  const rejected = data?.rejected_invites ?? 0;

  const talentsAwaitingReview = priorityActions?.talents_awaiting_review;
  const interviewsScheduled = priorityActions?.interviews_scheduled;
  const jobsExpiringSoon = priorityActions?.jobs_expiring_soon;

  const calculatePercentage = (value: number) => {
    if (!total || total === 0) return 0;
    return Math.round((value / total) * 100);
  };


  useEffect(() => {
    const recruiterProgressShown = localStorage.getItem("recruiterProgressShown");
    const lastShownTime = localStorage.getItem("recruiterProgressLastShown");
    const currentTime = new Date().getTime();

    if (recruiterProgressShown && lastShownTime && currentTime - Number(lastShownTime) < 24 * 60 * 60 * 1000) {
      return;
    }


    const timer = setTimeout(() => {
      if (data?.profile.total !== 100) {
        setIsOpen(true);

        localStorage.setItem("recruiterProgressShown", "true");
        localStorage.setItem("recruiterProgressLastShown", currentTime.toString());
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [data]);

  // Don't block the whole dashboard if priority-actions fails; show invite cards first.
  if (isLoading) {
    return <DashboardShimmer />;
  }


  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {isOpen && (
          <RecruiterProgress
            open={isOpen}
            data={data?.profile}
            handleClose={() => setIsOpen(false)}
          />
        )}

        <HomeCard
          title="Accepted"
          count={accepted}
          percentage={calculatePercentage(accepted)}
        />

        <HomeCard
          title="Pending"
          count={pending}
          percentage={calculatePercentage(pending)}
        />

        <HomeCard
          title="Rejected"
          count={rejected}
          percentage={calculatePercentage(rejected)}
        />
      </div>

      <div className="mt-5">
        <h2 className="text-base font-medium text-gray-800">
          Priority Actions
        </h2>

        <p className="text-xs text-gray-400 mt-0.5">
          Focus on what needs your attention today.
        </p>

        {isPriorityLoading && (
          <p className="text-xs text-gray-400 mt-2">Loading priority actions...</p>
        )}

        {isPriorityError && (
          <p className="text-xs text-red-500 mt-2">
            Couldn’t load priority actions. Please refresh.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
          <PriorityActionCard
            title="Talents Awaiting Review"
            description="Applicants are waiting for your review."
            count={talentsAwaitingReview?.count ?? 0}
            countClassName="text-orange-500"
            icon="ri-user-search-line"
            iconClassName="text-orange-500"
            iconBgClassName="bg-orange-50"
            items={[
              {
                label: "New Applicants",
                value: talentsAwaitingReview?.new_applicants ?? 0,
                valueClassName: "text-green-600",
              },
              {
                label: "Shortlisted Talents",
                value: talentsAwaitingReview?.shortlisted_talents ?? 0,
                valueClassName: "text-green-600",
              },
              {
                label: "Recommended",
                value: talentsAwaitingReview?.recommended ?? 0,
                valueClassName: "text-green-600",
              },
            ]}
            footerClassName="text-orange-500"
          />

          <PriorityActionCard
            title="Interviews Scheduled"
            description="Upcoming interviews this week."
            count={interviewsScheduled?.count ?? 0}
            countClassName="text-green-600"
            icon="ri-calendar-line"
            iconClassName="text-green-600"
            iconBgClassName="bg-green-50"
            items={[
              {
                label: "Today",
                value: interviewsScheduled?.today ?? 0,
              },
              {
                label: "Tomorrow",
                value: interviewsScheduled?.tomorrow ?? 0,
              },
              {
                label: "This Week",
                value: interviewsScheduled?.this_week ?? 0,
              },
            ]}
            footerText="View Interviews"
            footerClassName="text-green-600"
            onClick={() => navigate(routePath.recruierInvites)}
          />

          <PriorityActionCard
            title="Jobs Expiring Soon"
            description="Jobs closing in the next 7 days."
            count={jobsExpiringSoon?.count ?? 0}
            countClassName="text-red-500"
            icon="ri-time-line"
            iconClassName="text-red-500"
            iconBgClassName="bg-red-50"
            items={[
              {
                label: "Within 2 Days",
                value: jobsExpiringSoon?.within_2_days ?? 0,
                valueClassName: "text-red-500",
              },
              {
                label: "Within 5 Days",
                value: jobsExpiringSoon?.within_5_days ?? 0,
                valueClassName: "text-orange-500",
              },
              {
                label: "Within 7 Days",
                value: jobsExpiringSoon?.within_7_days ?? 0,
                valueClassName: "text-gray-800",
              },
            ]}
            footerText="View Jobs"
            footerClassName="text-red-500"
            onClick={() => navigate(routePath.jobs)}
          />
        </div>
      </div>

      <RecruiterChart stats={data} />
    </>
  );
};
